import { motion } from 'framer-motion'
import { LayoutGrid } from 'lucide-react'

import { DashboardDonut, DashboardDonutLegend } from '@/components/dashboard/DashboardDonut'
import { useCommandDashboard } from '@/contexts/CommandDashboardContext'
import { floorMixChartData } from '@/lib/dashboard/dashboardChartData'
import { mapFloorRooms, mapFloorStats } from '@/lib/commandMappers'
import { cn } from '@/lib/utils'

const MAP_STYLES = {
  occupied: 'bg-primary text-primary-foreground shadow-md shadow-primary/20',
  available: 'bg-emerald-500 text-white shadow-md shadow-emerald-500/20',
  turnover: 'bg-amber-400 text-amber-950 shadow-md shadow-amber-400/20',
}

export function RoomOccupancyOverview() {
  const { data } = useCommandDashboard()
  const floorStats = mapFloorStats(data?.floor)
  const rooms = mapFloorRooms(data?.floor?.rooms)
  const floorData = floorMixChartData(data?.floor)

  return (
    <section className="card-surface overflow-hidden p-4 lg:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <LayoutGrid className="h-4 w-4" strokeWidth={2.25} />
          </div>
          <div>
            <h2 className="text-sm font-bold tracking-tight">Floor map</h2>
            <p className="text-[11px] text-muted-foreground">{floorStats.occupancyPct}% load today</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 text-[11px]">
          <span>
            <strong className="text-primary">{floorStats.occupied}</strong>{' '}
            <span className="text-muted-foreground">occupied</span>
          </span>
          <span>
            <strong className="text-emerald-600">{floorStats.available}</strong>{' '}
            <span className="text-muted-foreground">available</span>
          </span>
          <span>
            <strong className="text-amber-600">{floorStats.turnover}</strong>{' '}
            <span className="text-muted-foreground">turnover</span>
          </span>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_200px] lg:items-center">
        <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-3 [&::-webkit-scrollbar]:hidden">
          {rooms.map((room, i) => (
            <motion.div
              key={room.id}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.03 }}
              title={`${room.fullName} · ${room.status}`}
              className={cn(
                'group relative flex h-14 min-w-[64px] max-w-[88px] flex-1 flex-col items-center justify-center rounded-2xl text-[10px] font-bold transition-transform hover:scale-105 sm:h-16 sm:min-w-[72px]',
                MAP_STYLES[room.status],
              )}
            >
              {room.status === 'occupied' && (
                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-white animate-pulse-live shadow-sm" />
              )}
              <span>{room.name}</span>
              {room.occupancy > 0 && (
                <span className="text-[8px] font-medium opacity-85">{room.occupancy}%</span>
              )}
            </motion.div>
          ))}
        </div>

        <div className="rounded-2xl bg-muted/20 p-3 sm:max-w-xs lg:max-w-none">
          <DashboardDonut
            data={floorData}
            centerLabel="Load"
            centerValue={`${floorStats.occupancyPct}%`}
            height={140}
            emptyLabel="No rooms"
          />
          <DashboardDonutLegend data={floorData} />
        </div>
      </div>
    </section>
  )
}
